import React, { useEffect, useRef, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Box, Center, Divider, Heading, Link, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SchneiderHamIcon } from 'clipsal-cortex-icons/src/custom-icons';

import { ArcButton } from '../../common/components/ArcButton';
import { SEHomeCard } from '../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { COMPONENT_MIN_HEIGHT, TOP_NAV_SPACING_AFFORDANCE } from '../../common/constants';
import { useParamBackUrl } from '../../common/hooks/use-param-back-url';
import { selectEvChargers } from '../site/siteSlice';
import { AccountMenuButton } from './AccountMenuButton';

const COLOR_ARC = '#3DCD58';
const COLOR_BUTTON_FILL = '#505559';
const COLOR_GET_CHARGER_ID = '#1E79B6';

const SUPPORT_WEB_URLS: Record<string, string> = {
  'en-US': 'https://www.se.com/us/en/work/support/',
  'es-US': 'https://www.se.com/us/en/work/support/',
  'en-CA': 'https://www.se.com/ca/en/work/support/',
  'fr-CA': 'https://www.se.com/ca/fr/work/support/',
};

const FAQ_WEB_URLS: Record<string, string> = {
  'en-US': 'https://www.se.com/us/en/work/support/schneider-charge/',
  'es-US': 'https://www.se.com/us/en/work/support/schneider-charge-es/',
  'en-CA': 'https://www.se.com/ca/en/work/support/schneider-charge/',
  'fr-CA': 'https://www.se.com/ca/fr/work/support/schneider-charge/',
};

const DEFAULT_SUPPORT_WEB_URL = SUPPORT_WEB_URLS['en-US'];
const DEFAULT_FAQ_WEB_URL = FAQ_WEB_URLS['en-US'];

function getSupportWebUrl(language: string) {
  return SUPPORT_WEB_URLS[language] ?? DEFAULT_SUPPORT_WEB_URL;
}

function getFaqWebUrl(language: string) {
  return FAQ_WEB_URLS[language] ?? DEFAULT_FAQ_WEB_URL;
}

export const Support = () => {
  const backURL = useParamBackUrl('../dashboard?direction=back');
  const iconColor = useColorModeValue('primaryBranding.600', 'primaryBranding.500');
  const scrollFadeColor = useColorModeValue('rgba(255,255,255,0.95)', 'rgba(17,17,17,0.95)');
  const { t } = useTranslation();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  function handleScrollCheck() {
    const el = scrollRef.current;
    if (!el) return;
    setHasMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }

  useEffect(() => {
    handleScrollCheck();
    window.addEventListener('resize', handleScrollCheck);
    return () => window.removeEventListener('resize', handleScrollCheck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SlidingAnimationPageBase title={t('Support.support')} backURL={backURL}>
      <Box
        display="flex"
        flexDirection="column"
        h={`calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE})`}
        fontFamily="-apple-system, 'SF Pro Text', BlinkMacSystemFont, sans-serif"
      >
        {/* Scrollable content */}
        <Box flex={1} position="relative" minH={0} overflow="hidden">
          <Box ref={scrollRef} h="100%" overflowY="auto" onScroll={handleScrollCheck} px={5} pb={4}>
            <Center>
              <SchneiderHamIcon w={'100px'} h={'100px'} color={iconColor} my={4} />
            </Center>
            <ContactDetails />
          </Box>

          {hasMoreBelow && (
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              h="56px"
              background={`linear-gradient(to bottom, transparent, ${scrollFadeColor})`}
              pointerEvents="none"
            />
          )}
        </Box>

        {/* Pinned bottom — ArcButton + link, always visible */}
        <ContactActions />
      </Box>
    </SlidingAnimationPageBase>
  );
};

export const ContactDetails = () => {
  const { t } = useTranslation();

  const selectedLanguage = localStorage.getItem('i18nextLng') ?? 'en-US';
  const supportWebUrl = getSupportWebUrl(selectedLanguage);

  return (
    <>
      <Heading fontWeight="bold" mb={2} fontSize="24px" textAlign="center" data-testid="contact-support-heading">
        {t('Common.contact support')}
      </Heading>
      <Text fontWeight={400} fontSize="16px" mb={5} textAlign="center" px={7}>
        {t('Support.customer care teams')}
      </Text>

      <Divider mb={5} borderColor="gray.500" />

      <SEHomeCard mb={5} borderTopLeftRadius={'5px'} p={0}>
        <AccountMenuButton
          includeBottomDivider={false}
          data-testid="support-faq"
          onClick={() => {
            Browser.open({ url: getFaqWebUrl(selectedLanguage) });
          }}
        >
          <Text fontSize={16} fontWeight="normal">
            {t('Support.faq')}
          </Text>
        </AccountMenuButton>
      </SEHomeCard>

      <Text fontSize="md" fontWeight={600} mb={1}>
        {t('Support.phone support')}
      </Text>
      <Text fontWeight={400} fontSize="16px">
        <Link data-testid={'support-phone-1'} isExternal href={'tel:+18773425173'} fontWeight={400}>
          (877) 342 5173 (USA)
        </Link>
      </Text>
      <Text fontWeight={400} fontSize="16px">
        <Link data-testid={'support-phone-2'} isExternal href={'tel:+18005656699'} fontWeight={400}>
          1 (800) 565 6699 (Canada)
        </Link>
      </Text>
      <Text fontWeight={400} fontSize="16px" mt={1} mb={5}>
        {t('Support.available mon fri')}
      </Text>

      <Text fontSize="16px" fontWeight={600}>
        {t('Support.web support')}
      </Text>
      <Link
        data-testid={'support-link'}
        isExternal
        href={supportWebUrl}
        textDecoration="underline"
        fontWeight={400}
        fontSize="16px"
        mb={8}
        display="block"
      >
        {supportWebUrl}
      </Link>
    </>
  );
};

const ContactActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const buttonFill = useColorModeValue(COLOR_BUTTON_FILL, COLOR_BUTTON_FILL);
  const selectedLanguage = localStorage.getItem('i18nextLng') ?? 'en-US';
  const hasCharger = useSelector(selectEvChargers).length > 0;

  return (
    <Center flexDirection="column" flexShrink={0} py={3}>
      <ArcButton
        arcColor={COLOR_ARC}
        background={buttonFill}
        _hover={{ background: buttonFill }}
        borderColor={buttonFill}
        sx={{ '> div': { background: `${COLOR_BUTTON_FILL} !important` } }}
        color="white"
        w="calc(100% - var(--chakra-space-12))"
        mb={2}
        data-testid="contact-support-button"
        onClick={() => {
          Browser.open({ url: getSupportWebUrl(selectedLanguage) });
        }}
      >
        <Text as="span">{t('Common.contact support')}</Text>
      </ArcButton>

      {hasCharger && (
        <Link
          data-testid="get-charger-id-link"
          color={COLOR_GET_CHARGER_ID}
          fontWeight="bold"
          fontSize="sm"
          onClick={() => navigate('../../home/device-details?direction=forward&backURL=../../account/support')}
          cursor="pointer"
          mb={2}
        >
          {t('Support.get charger id')}
        </Link>
      )}
    </Center>
  );
};
